import { useEffect, useState } from "react";
import { BlockStack, Button, Card, InlineStack, Text } from "@shopify/polaris";

import { hasOwnConciergeVoice } from "./OnboardingChecklist";
import {
  INSTALL_STATUS,
  type CreditState,
  type ShopMe,
} from "../services/brilio";

/**
 * The app's only request for an App Store review.
 *
 * The listing has zero reviews and Built for Shopify wants five plus a rating
 * threshold, so this card exists. What it must not become is the thing that
 * fills this category: install-day prompts that harvest "used it for 38
 * minutes" three-star reviews from merchants with nothing yet to review.
 * Shopify says the same in its own words —
 * shopify.dev/docs/apps/launch/marketing/manage-app-reviews: "Don't ask for
 * reviews when a merchant is onboarding or installing your app", don't offer
 * "a discount or other benefit in exchange for leaving a review", don't ask for
 * POSITIVE reviews, don't block app functionality, and "don't re-ask after they
 * decline".
 *
 * So the gate below is the design, and it is EVIDENCE: shoppers have actually
 * been answered, on a store that has had the app for more than a week, with
 * setup finished, and with the page not simultaneously telling the merchant
 * something is wrong. Never a timer that fires an ask on its own.
 */

/**
 * How many answered shopper messages count as "you have something to review".
 *
 * `messages_used` is incremented once per turn the model actually ANSWERED, on
 * the storefront chat proxy only (api/routes/shopify_proxy.py
 * `_record_message_used`, awaited after `create_interaction` returns) — it is
 * the only writer in the backend, and nothing ever resets it. So it counts
 * value delivered, not installs, page views or API calls, and the threshold is
 * crossed once and stays crossed: the card cannot flicker in and out between
 * page loads.
 *
 * Ten answered turns is several real shopper conversations — enough to have an
 * opinion, and unreachable by a merchant who is still setting up. It is
 * deliberately NOT also carrying the job of excluding install day; MIN_DAYS
 * below does that, which is what lets this number be honest about experience
 * rather than inflated as a proxy for elapsed time.
 *
 * This is a judgement, not a measurement: there is no per-shop usage data in
 * this app, and the number a review ask ADMITS matters as much as the number it
 * excludes — a gate that admits nobody fails the goal as surely as an
 * install-day prompt does.
 */
export const MIN_ANSWERED_MESSAGES = 10;

/**
 * How long the store must have had the app before it is asked anything.
 *
 * A busy storefront can index policies, rename the assistant and take ten
 * answered messages within hours of installing, which lands the ask squarely
 * inside the window Shopify names as a violation. `period_start` is written
 * once, in `_create_first_install` alongside `installed_at`
 * (api/controllers/shopify_app.py) and by nothing else — a reinstall goes
 * through `_refresh_existing_install`, which does not touch it — so despite the
 * name it never rolls over and is a true install date.
 *
 * A suppressor, never a trigger: seven days of ownership on its own shows this
 * card to nobody.
 */
export const MIN_DAYS_SINCE_INSTALL = 7;

/**
 * How many times a merchant who neither accepts nor declines may see this.
 *
 * The explicit "no" is honoured for ever, but silence is an answer too: an ask
 * that renders on every visit until someone presses a button is a permanent
 * fixture on the homepage, which is the same complaint from the other side.
 * Three sightings, then it is gone for good.
 */
export const MAX_APPEARANCES = 3;

/**
 * Where the answer lives — and there is nowhere else.
 *
 * This app has no per-merchant preference store. The only writable endpoint is
 * PATCH /shopify/admin/shops/me/concierge, and every field it accepts (persona,
 * greeting, agent_name, is_active, suggested_questions, handoff_email) is
 * something a SHOPPER sees; smuggling a UI flag into one of those would corrupt
 * the storefront in order to remember a banner.
 *
 * The consequences, stated plainly because they are the compromise:
 *  - Per browser profile and per device. Dismiss in Chrome, open the admin on a
 *    phone later, and the card can appear once more there.
 *  - An embedded app runs in a third-party iframe, so this storage is
 *    partitioned by the admin's top-level site. That is exactly why this is ONE
 *    key and not one per shop: an agency's stores share the partition under
 *    admin.shopify.com, so someone who says no once is not asked again for the
 *    next store. On the legacy {store}.myshopify.com/admin host the partition
 *    is per store, and there it degrades to per-shop.
 *  - Clearing site data forgets it. The gate still holds on every re-ask, so
 *    the worst case is one more dismissible card at the foot of the page —
 *    never an install-day prompt.
 * "Once per merchant" is therefore an approximation, and this is where it leaks.
 *
 * Named like the widget's own keys (`brilio_concierge_conversation`,
 * `brilio_concierge_config`) so all of this app's storage sorts together.
 */
export const REVIEW_ASK_KEY = "brilio_concierge_review_ask";

/** What we remember about asking this merchant. */
interface AskMemory {
  /** Page loads that have actually shown the card. */
  shown: number;
  /** When they pressed "Don't ask again". Presence means never again. */
  declined?: string;
  /** When they opened the review form. */
  clicked?: string;
}

const NEVER_ASKED: AskMemory = { shown: 0 };

/**
 * Per-page-load memory, deliberately OUTSIDE React.
 *
 * Without it a "no" lasts only as long as the mounted component. Dashboard is a
 * client-side route (src/App.tsx), so Conversations -> Dashboard remounts this
 * card, and for the merchants whose browser refuses storage — Firefox strict
 * ETP, Safari with cross-site tracking prevention, both of which throw inside a
 * third-party iframe — the ask would be back seconds after they declined it.
 * `localStorage` covers the reload; this covers the route change when
 * `localStorage` is not available to.
 */
let silencedThisPageLoad = false;
/** So an SPA route change back to the Dashboard does not burn a second sighting. */
let countedThisPageLoad = false;

/**
 * Forget this page load. Exported for tests ONLY — module state survives
 * `cleanup()`, so without it one test's dismissal silently answers the next,
 * and no test could distinguish "held across a route change" (required) from
 * "held across a reload" (which, with storage denied, it cannot be).
 */
export function __resetReviewAskSession(): void {
  silencedThisPageLoad = false;
  countedThisPageLoad = false;
}

/**
 * The app's listing, deep-linked to the write-a-review modal.
 *
 * `#modal-show=WriteReviewModal` is Shopify's own documented form —
 * shopify.dev/docs/apps/launch/marketing/manage-app-reviews gives
 * `https://apps.shopify.com/[your-app]#modal-show=WriteReviewModal` verbatim —
 * and https://apps.shopify.com/brilio-concierge answers 200 today.
 *
 * Built from `app_handle`, the same field `pricingPlansUrl` builds the
 * managed-pricing URL from: the backend sends the one handle both URLs key off
 * (`config.shopify_app_handle or "brilio-concierge"`). Same contract too — null
 * when it is missing, and the caller renders nothing rather than a link to
 * nowhere.
 *
 * Validated against the handle vocabulary rather than escaped:
 * encodeURIComponent would turn a wrong value into a percent-escaped URL that
 * still looks like a link and still 404s.
 */
export function reviewUrlFor(appHandle: string | undefined): string | null {
  if (!appHandle || !/^[a-z0-9][a-z0-9-]*$/i.test(appHandle)) return null;
  return `https://apps.shopify.com/${appHandle}#modal-show=WriteReviewModal`;
}

/**
 * Is the Dashboard's credit banner on screen?
 *
 * One definition, shared with `CreditWarning`, so the banner and the card that
 * has to hide behind it cannot drift apart. The failure being designed out is
 * this codebase's most repeated one: someone adds a fifth CreditState, updates
 * the banner, and the review ask goes on asking for a review over the top of
 * "your assistant has stopped answering".
 *
 * Its proper home is services/brilio.ts, beside the `CreditState` type both
 * callers already import — a merchant-critical banner should not depend on the
 * review-ask module. That file was out of scope for this change; moving this
 * function there is a one-line follow-up and nothing else has to change.
 *
 * `undefined` is an API build predating the field, and it renders no banner —
 * so it is not a warning here either.
 */
export function hasCreditWarning(
  state: CreditState | undefined,
): state is Exclude<CreditState, "ok"> {
  return !!state && state !== "ok";
}

/**
 * Has this store had the app long enough to be asked?
 *
 * Anything unreadable — absent, null, or a date that will not parse — answers
 * NO. "We cannot tell how long they have had it" must never resolve to "ask
 * them now"; that is precisely the install-day case.
 */
export function installedLongEnough(
  periodStart: string | null | undefined,
  days: number = MIN_DAYS_SINCE_INSTALL,
): boolean {
  if (!periodStart) return false;
  const installedAt = Date.parse(periodStart);
  if (Number.isNaN(installedAt)) return false;
  return Date.now() - installedAt >= days * 24 * 60 * 60 * 1000;
}

function readMemory(): AskMemory {
  try {
    const raw = window.localStorage.getItem(REVIEW_ASK_KEY);
    if (!raw) return NEVER_ASKED;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return NEVER_ASKED;
    // Field by field, because this string is whatever is in the browser: an
    // older shape of this record, or something else entirely under a key a
    // merchant's extension happened to reuse. A `shown` that is not a number
    // would make `shown + 1` produce "12" and the cap stop working silently.
    const record = parsed as Partial<AskMemory>;
    return {
      shown:
        typeof record.shown === "number" && record.shown >= 0
          ? record.shown
          : 0,
      declined:
        typeof record.declined === "string" ? record.declined : undefined,
      clicked: typeof record.clicked === "string" ? record.clicked : undefined,
    };
  } catch {
    // Safari with third-party storage blocked throws on ACCESS, not only on
    // write, and JSON.parse throws on anything malformed. Failing open (ask)
    // rather than closed (never ask) is deliberate: the gate is what protects
    // the merchant, and an unreadable key must not silently disable the only
    // review ask the app has. `silencedThisPageLoad` is what makes their "no"
    // stick meanwhile.
    return NEVER_ASKED;
  }
}

function writeMemory(next: AskMemory): void {
  try {
    window.localStorage.setItem(REVIEW_ASK_KEY, JSON.stringify(next));
  } catch {
    // Nothing to do, and nothing to report: the caller has already silenced the
    // card for this page load, so the merchant's answer is honoured now and
    // forgotten on the next full reload.
  }
}

export function ReviewAsk({ shop }: { shop: ShopMe }) {
  const [silenced, setSilenced] = useState(() => silencedThisPageLoad);
  // Read once per mount, through a lazy initialiser, so the decision this
  // render makes cannot change under it — and so a remount re-reads storage.
  const [memory] = useState(readMemory);

  const reviewUrl = reviewUrlFor(shop.app_handle);
  const answered = shop.messages_used;

  /**
   * Every condition is a fact this page ALREADY holds — no request was added to
   * fetch a number, and nothing here fires on elapsed time alone.
   *
   * The one setup step missing from this list is the theme app embed, readable
   * only through an async App Bridge call that answers "unknown" often enough
   * to have its own badge. It is not needed: `messages_used` moves only when a
   * shopper talks to the widget ON the storefront, so ten answered messages is
   * stronger evidence the embed is live than App Bridge's own answer.
   */
  const earned =
    // Not reachable through today's Dashboard, which has already returned for
    // every non-active status — but this component must be safe to mount
    // anywhere, and an uninstalled shop is the last one to ask for a review.
    shop.install_status === INSTALL_STATUS.active &&
    // A merchant who has switched the assistant off is mid-problem, not
    // mid-delight.
    shop.concierge.is_active &&
    // Never over the top of the page telling them their store is losing sales.
    !hasCreditWarning(shop.credit_state) &&
    // The two setup steps the page can prove from data it holds. While either
    // is outstanding the checklist above is still saying "finish setting up
    // your assistant", and asking for a review over that is the same defect as
    // asking over the credit banner.
    (shop.policies_indexed ?? 0) > 0 &&
    hasOwnConciergeVoice(shop.concierge, shop.shop_domain) &&
    answered >= MIN_ANSWERED_MESSAGES &&
    installedLongEnough(shop.period_start);

  const askable =
    earned &&
    reviewUrl !== null &&
    memory.declined === undefined &&
    memory.shown < MAX_APPEARANCES;
  const visible = askable && !silenced;

  useEffect(() => {
    if (!visible || countedThisPageLoad) return;
    countedThisPageLoad = true;
    writeMemory({ ...memory, shown: memory.shown + 1 });
  }, [visible, memory]);

  if (!visible || reviewUrl === null) return null;

  /**
   * Stop showing it.
   *
   * "declined" is for ever — Shopify's guidance is explicit that a merchant who
   * says no is not re-asked. "clicked" is not a no: they have been handed the
   * form, so asking again on this page load would be absurd, but a blocked
   * popup or a closed tab means they may never have written anything. It leaves
   * exactly one more sighting rather than retiring the card on a click that may
   * have gone nowhere.
   *
   * Storage is re-read here rather than reusing the mount snapshot: the effect
   * above has already written `shown + 1`, and spreading the stale value would
   * hand that sighting back.
   */
  const stopAsking = (answer: "declined" | "clicked") => () => {
    const now = new Date().toISOString();
    const current = readMemory();
    writeMemory(
      answer === "declined"
        ? { ...current, declined: now }
        : {
            ...current,
            clicked: now,
            // `Math.max`, so clicking late cannot ADD sightings back.
            shown: Math.max(current.shown, MAX_APPEARANCES - 1),
          },
    );
    silencedThisPageLoad = true;
    setSilenced(true);
  };

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h3" variant="headingSm">
          Would you review Brilio Assistant?
        </Text>
        {/* Rating-neutral on purpose. Shopify names asking for POSITIVE reviews
            as a violation in its own right, and "good or bad" is the line that
            keeps this an honest request rather than a bid. The message count is
            here because it is the evidence they are being asked about — the ask
            is specific for the same reason the gate is. */}
        <Text as="p" tone="subdued">
          Your assistant has answered {answered.toLocaleString()} shopper
          messages on {shop.shop_domain}. If you have a couple of minutes, an
          honest review on the Shopify App Store helps other merchants judge
          whether it suits their store — good or bad, we read every one.
        </Text>
        <InlineStack gap="200" blockAlign="center">
          {/* `external`, so Polaris pairs the new tab with
              rel="noopener noreferrer" itself — and a new tab is required
              either way: this app renders inside the admin's iframe. NOT a
              top-frame redirect like the plan buttons: sending a merchant's
              whole admin to the App Store because they read a card is not a
              thing to do to them. */}
          <Button url={reviewUrl} external onClick={stopAsking("clicked")}>
            Leave a review
          </Button>
          {/* "Don't ask again", not "Not now": it never comes back, and the
              label should say what the button does. Shopify's guidance asks for
              exactly this affordance. */}
          <Button variant="plain" onClick={stopAsking("declined")}>
            Don&rsquo;t ask again
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}
